import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useState } from 'react'

import { type StackedBarChartSegmentData } from '@/pages/ui/stacked-bar-chart/types'

import { type SelectFilterOptionValue } from '@/components/select-filter/constants'
import { SELECT_FILTER_OPTION_VALUE_MAP } from '@/components/select-filter/constants'

import { getCaseStatusBreakdownQuery } from '@/lib/queries/business/analysis/queries'
import { CASE_ARCHIVE_STATUS_MAP } from '@/lib/queries/business/constants'

const useCaseStatusBreakdown = () => {
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedActiveInactive, setSelectedActiveInactive] = useState<string[]>(['active'])
  const [selectedTagCondition, setSelectedTagCondition] = useState<SelectFilterOptionValue>(
    SELECT_FILTER_OPTION_VALUE_MAP.or
  )

  const archiveStatus = useMemo(() => {
    const len = selectedActiveInactive.length
    if (len === 2 || len === 0) return CASE_ARCHIVE_STATUS_MAP.all
    if (selectedActiveInactive.includes('active')) return CASE_ARCHIVE_STATUS_MAP.active
    return CASE_ARCHIVE_STATUS_MAP.archived
  }, [selectedActiveInactive])

  const { data: dataCaseStatus, isFetching: isFetchingCaseStatus } = useQuery(
    getCaseStatusBreakdownQuery({
      assignees: selectedAssignees,
      tags: selectedTags,
      archiveStatus,
      tagCondition: selectedTagCondition,
    })
  )

  const dataCaseStatus_ = useMemo(() => {
    const dol = (dataCaseStatus?.dol.results ?? []).map((x) => ({
      name: x.key,
      value: x.count,
      ratio: x.ratio,
    }))
    const nvc = (dataCaseStatus?.nvc.results ?? []).map((x) => ({
      name: x.key,
      value: x.count,
      ratio: x.ratio,
    }))
    const uscis = (dataCaseStatus?.uscis.results ?? []).map((x) => ({
      name: x.key,
      value: x.count,
      ratio: x.ratio,
    }))
    const eoir = (dataCaseStatus?.eoir.results ?? []).map((x) => ({
      name: x.key,
      value: x.count,
      ratio: x.ratio,
    }))
    const totalDol = dol.reduce((acc, x) => acc + x.value, 0)
    const totalNvc = nvc.reduce((acc, x) => acc + x.value, 0)
    const totalUscis = uscis.reduce((acc, x) => acc + x.value, 0)
    const totalEoir = eoir.reduce((acc, x) => acc + x.value, 0)
    return { dol, nvc, uscis, eoir, totalDol, totalNvc, totalUscis, totalEoir }
  }, [dataCaseStatus])

  const handleReset = () => {
    setSelectedAssignees([])
    setSelectedTags([])
    setSelectedActiveInactive([])
  }

  const handleImmigrationStatusClick = (x: StackedBarChartSegmentData) => {
    window.open(`/cases/active?statuses=${x.name}`, '_blank')
  }

  const handleEoirStatusClick = (x: StackedBarChartSegmentData) => {
    window.open(`/cases-eoir/active?statuses=${x.name}`, '_blank')
  }

  return {
    selectedAssignees,
    setSelectedAssignees,
    selectedTags,
    setSelectedTags,
    selectedActiveInactive,
    setSelectedActiveInactive,
    selectedTagCondition,
    setSelectedTagCondition,
    isFetchingCaseStatus,
    dataCaseStatus: dataCaseStatus_,
    onFilterReset: handleReset,
    onImmigrationStatusClick: handleImmigrationStatusClick,
    onEoirStatusClick: handleEoirStatusClick,
  }
}

export default useCaseStatusBreakdown
